import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { productsApi } from '@/api/products'
import { Button } from '@/components/ui/button'
import { ListPagination } from '@/components/common/ListPagination'
import { StatusTabs } from './components/StatusTabs'
import { ProductFilters } from './components/ProductFilters'
import { ProductDetailDialog } from './components/ProductDetailDialog'
import { StockUpdateDialog } from './components/StockUpdateDialog'
import { StockRecordDialog } from './components/StockRecordDialog'

export function ProductsPage() {
	const [status, setStatus] = useState(1)
	const [fieldKey, setFieldKey] = useState('store_name')
	const [keyword, setKeyword] = useState('')
	const [cateId, setCateId] = useState(0)
	const [brandId, setBrandId] = useState(0)
	const [priceMin, setPriceMin] = useState('')
	const [priceMax, setPriceMax] = useState('')
	const [stockMin, setStockMin] = useState('')
	const [stockMax, setStockMax] = useState('')
	const [salesMin, setSalesMin] = useState('')
	const [salesMax, setSalesMax] = useState('')
	const [createTimeMin, setCreateTimeMin] = useState('')
	const [createTimeMax, setCreateTimeMax] = useState('')
	const [salesOrder, setSalesOrder] = useState('normal')
	const [specType, setSpecType] = useState(-1)
	const [page, setPage] = useState(1)
	const [pageSize, setPageSize] = useState(30)
	const [detailProductId, setDetailProductId] = useState<number | null>(null)
	const [stockProductId, setStockProductId] = useState<number | null>(null)
	const [recordProductId, setRecordProductId] = useState<number | null>(null)
	const [searchTrigger, setSearchTrigger] = useState(0)

	const { data: brands = [] } = useQuery({ queryKey: ['product-brands'], queryFn: productsApi.listBrands })
	const { data: categories = [] } = useQuery({ queryKey: ['product-categories'], queryFn: productsApi.listCategories })
	const { data, isLoading, isFetching } = useQuery({
		queryKey: ['products', { status, fieldKey, keyword, cateId, brandId, priceMin, priceMax, stockMin, stockMax, salesMin, salesMax, createTimeMin, createTimeMax, salesOrder, specType, page, pageSize, searchTrigger }],
		queryFn: () => productsApi.list({
			status, field_key: fieldKey, keyword, cate_id: cateId, brand_id: brandId,
			price_min: priceMin, price_max: priceMax,
			stock_min: stockMin ? Number(stockMin) : undefined,
			stock_max: stockMax ? Number(stockMax) : undefined,
			sales_min: salesMin ? Number(salesMin) : undefined,
			sales_max: salesMax ? Number(salesMax) : undefined,
			create_time_min: createTimeMin || undefined,
			create_time_max: createTimeMax || undefined,
			sales: salesOrder,
			spec_type: specType >= 0 ? specType : undefined,
			page, page_size: pageSize,
		}),
	})

	const handleSearch = () => { setPage(1); setSearchTrigger((v) => v + 1) }
	const handleReset = () => {
		setFieldKey('store_name'); setKeyword(''); setCateId(0); setBrandId(0)
		setPriceMin(''); setPriceMax(''); setStockMin(''); setStockMax('')
		setSalesMin(''); setSalesMax(''); setCreateTimeMin(''); setCreateTimeMax('')
		setSalesOrder('normal'); setSpecType(-1); setPage(1); setSearchTrigger((v) => v + 1)
	}
	const handleStatusChange = (newStatus: number) => { setStatus(newStatus); setPage(1) }

	return (
		<div className="space-y-4">
			<h1 className="text-2xl font-bold">商品管理</h1>
			<StatusTabs status={status} onChange={handleStatusChange} />
			<ProductFilters
				fieldKey={fieldKey} keyword={keyword} cateId={cateId} brandId={brandId}
				priceMin={priceMin} priceMax={priceMax} brands={brands} categories={categories}
				stockMin={stockMin} stockMax={stockMax}
				salesMin={salesMin} salesMax={salesMax}
				createTimeMin={createTimeMin} createTimeMax={createTimeMax}
				salesOrder={salesOrder}
				specType={specType}
				onFieldKeyChange={setFieldKey} onKeywordChange={setKeyword}
				onCateIdChange={setCateId} onBrandIdChange={setBrandId}
				onPriceMinChange={setPriceMin} onPriceMaxChange={setPriceMax}
				onStockMinChange={setStockMin} onStockMaxChange={setStockMax}
				onSalesMinChange={setSalesMin} onSalesMaxChange={setSalesMax}
				onCreateTimeMinChange={setCreateTimeMin} onCreateTimeMaxChange={setCreateTimeMax}
				onSalesOrderChange={setSalesOrder}
				onSpecTypeChange={setSpecType}
				onSearch={handleSearch} onReset={handleReset}
			/>
			<div className="border rounded-lg overflow-hidden">
				<table className="w-full text-sm">
					<thead className="bg-muted/50">
						<tr>
							<th className="text-left p-3">ID</th>
							<th className="text-left p-3">图片</th>
							<th className="text-left p-3">商品名称</th>
							<th className="text-right p-3">价格</th>
							<th className="text-right p-3">库存</th>
							<th className="text-right p-3">销量</th>
							<th className="text-left p-3">分类</th>
							<th className="text-center p-3">状态</th>
							<th className="text-center p-3">操作</th>
						</tr>
					</thead>
					<tbody>
						{isLoading && <tr><td colSpan={9} className="text-center p-8 text-muted-foreground">加载中...</td></tr>}
						{!isLoading && data?.list.length === 0 && <tr><td colSpan={9} className="text-center p-8 text-muted-foreground">暂无数据</td></tr>}
						{data?.list.map((product) => (
							<tr key={product.id} className="border-t hover:bg-muted/30">
								<td className="p-3">{product.id}</td>
								<td className="p-3"><img src={product.image} alt="" className="h-12 w-12 rounded object-cover" /></td>
								<td className="p-3 max-w-[200px] truncate">{product.store_name}</td>
								<td className="text-right p-3">¥{product.price}</td>
								<td className="text-right p-3">{product.real_stock ?? product.stock}</td>
								<td className="text-right p-3">{product.sales}</td>
								<td className="p-3">{product.cate_name || '-'}</td>
								<td className="text-center p-3">
									<span className={`text-xs px-2 py-0.5 rounded ${product.is_show ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'}`}>{product.is_show ? '上架' : '下架'}</span>
								</td>
								<td className="text-center p-3">
									<div className="flex justify-center gap-2">
										<Button variant="outline" size="sm" onClick={() => setDetailProductId(product.id)}>详情</Button>
										<Button variant="outline" size="sm" onClick={() => setStockProductId(product.id)}>库存</Button>
										<Button variant="outline" size="sm" onClick={() => setRecordProductId(product.id)}>记录</Button>
									</div>
								</td>
							</tr>
						))}
					</tbody>
				</table>
			</div>
			{data && (
				<ListPagination page={page} pageSize={pageSize} total={data.total} loading={isFetching}
					onPageChange={setPage} onPageSizeChange={(size) => { setPageSize(size); setPage(1) }} />
			)}
			<ProductDetailDialog productId={detailProductId} onClose={() => setDetailProductId(null)} />
			<StockUpdateDialog productId={stockProductId} onClose={() => setStockProductId(null)} />
			<StockRecordDialog productId={recordProductId} onClose={() => setRecordProductId(null)} />
		</div>
	)
}
